import { useCallback, useEffect, useState } from "react";
import { neon } from "@/integrations/neon";
import { getErrorMessage } from "@/utils/errors";

export interface ContactRecord {
  id: string;
  name: string | null;
  email: string;
}

interface UseContactsResult {
  contacts: ContactRecord[];
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  upsertContact: (payload: { id?: string; name?: string | null; email: string }) => Promise<ContactRecord | null>;
  deleteContact: (contactId: string) => Promise<void>;
}

export const useContacts = (): UseContactsResult => {
  const [contacts, setContacts] = useState<ContactRecord[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const loadContacts = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const data = await neon.api.contacts.list();
      setContacts(data ?? []);
    } catch (error: unknown) {
      console.error("Failed to load contacts", error);
      setError(getErrorMessage(error, "Unable to load contacts"));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadContacts();
  }, [loadContacts]);

  const upsertContact: UseContactsResult["upsertContact"] = useCallback(async ({ id, name, email }) => {
    try {
      const data = await neon.api.contacts.upsert({ id, name, email });

      if (data) {
        setContacts((prev) => {
          const filtered = prev.filter((item) => item.id !== data.id && item.email !== data.email);
          return [...filtered, data].sort((a, b) => {
            const nameA = a.name?.toLowerCase() ?? "";
            const nameB = b.name?.toLowerCase() ?? "";
            if (nameA === nameB) {
              return a.email.localeCompare(b.email);
            }
            return nameA.localeCompare(nameB);
          });
        });
      }

      return data ?? null;
    } catch (err) {
      console.error("Failed to upsert contact", err);
      throw err;
    }
  }, []);

  const deleteContact: UseContactsResult["deleteContact"] = useCallback(async (contactId) => {
    try {
      await neon.api.contacts.delete(contactId);
      setContacts((prev) => prev.filter((contact) => contact.id !== contactId));
    } catch (err) {
      console.error("Failed to delete contact", err);
      throw err;
    }
  }, []);

  return {
    contacts,
    isLoading,
    error,
    refresh: loadContacts,
    upsertContact,
    deleteContact,
  };
};
